import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { apiRequest } from '@/utils/api-request';
import { Youtube, RefreshCw, Check, CloudUpload, ExternalLink, Loader2, Filter, Link2, Upload } from 'lucide-react';
import LinkToSchools from './LinkToSchools';
import UploadVideos from './UploadVideos';

interface Video {
  video_id: string;
  title: string;
  description: string;
  published_at: string;
  thumbnail_medium: string;
  thumbnail_high: string;
  video_url: string;
  duration_formatted: string;
  view_count: number;
  like_count: number;
  channel_key: string;
  channel_name: string;
  city_id: number;
  city_name: string;
  is_synced: boolean;
}

interface Channel {
  key: string;
  name: string;
  city_id: number;
  city_name: string;
}

interface FetchResponse {
  videos: Video[];
  total: number;
  synced_count: number;
  unsynced_count: number;
  channels: Channel[];
  date_range: { start: string; end: string };
}

const YoutubeVideosAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'sync' | 'link' | 'upload'>('sync');
  const [videos, setVideos] = useState<Video[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [syncingVideoId, setSyncingVideoId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);

  // Filters
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [syncFilter, setSyncFilter] = useState<string>('all');

  // Fetch all videos on mount
  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const response = await apiRequest<FetchResponse>('/youtube-videos/fetch-all', {
        method: 'GET',
      });

      if (response.success && response.data) {
        setVideos(response.data.videos);
        setChannels(response.data.channels);
        setDateRange(response.data.date_range);
      } else {
        toast.error(response.message || 'Failed to fetch videos');
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to fetch videos');
    } finally {
      setLoading(false);
    }
  };

  // Filter videos
  const filteredVideos = useMemo(() => {
    return videos.filter(video => {
      const matchesChannel = channelFilter === 'all' || video.channel_key === channelFilter;
      const matchesSync = syncFilter === 'all' ||
        (syncFilter === 'synced' && video.is_synced) ||
        (syncFilter === 'unsynced' && !video.is_synced);
      return matchesChannel && matchesSync;
    });
  }, [videos, channelFilter, syncFilter]);

  // Get unsynced videos for bulk sync
  const unsyncedVideos = useMemo(() => {
    const filtered = channelFilter === 'all'
      ? videos.filter(v => !v.is_synced)
      : videos.filter(v => !v.is_synced && v.channel_key === channelFilter);
    return filtered;
  }, [videos, channelFilter]);

  // Sync all unsynced videos
  const handleSyncAll = async () => {
    if (unsyncedVideos.length === 0) {
      toast.info('All videos are already synced');
      return;
    }

    setSyncing(true);
    try {
      const response = await apiRequest('/youtube-videos/sync', {
        method: 'POST',
        body: { videos: unsyncedVideos },
      });

      if (response.success && response.data) {
        toast.success(`Synced ${response.data.insertedCount} videos`);

        // Update local state to mark videos as synced
        const syncedIds = new Set(response.data.syncedVideoIds);
        setVideos(prev => prev.map(v =>
          syncedIds.has(v.video_id) ? { ...v, is_synced: true } : v
        ));
      } else {
        toast.error(response.message || 'Failed to sync videos');
      }
    } catch (error) {
      console.error('Error syncing videos:', error);
      toast.error('Failed to sync videos');
    } finally {
      setSyncing(false);
    }
  };

  // Sync single video
  const handleSyncVideo = async (video: Video) => {
    setSyncingVideoId(video.video_id);
    try {
      const response = await apiRequest('/youtube-videos/sync', {
        method: 'POST',
        body: { videos: [video] },
      });

      if (response.success && response.data) {
        toast.success('Video synced successfully');

        // Update local state
        setVideos(prev => prev.map(v =>
          v.video_id === video.video_id ? { ...v, is_synced: true } : v
        ));
      } else {
        toast.error(response.message || 'Failed to sync video');
      }
    } catch (error) {
      console.error('Error syncing video:', error);
      toast.error('Failed to sync video');
    } finally {
      setSyncingVideoId(null);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const syncedCount = videos.filter(v => v.is_synced).length;
  const unsyncedCount = videos.filter(v => !v.is_synced).length;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex items-center gap-4">
          {/* Left Side - Icon, Title */}
          <div className="flex items-center gap-3 flex-1">
            <div className="flex-shrink-0">
              <div className="p-2 bg-brandColor rounded-lg text-white">
                <Youtube className="h-5 w-5" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-semibold text-gray-900">YouTube Videos</h1>
                {!loading && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                      {syncedCount} Synced
                    </span>
                    {unsyncedCount > 0 && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                        {unsyncedCount} Not Synced
                      </span>
                    )}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {dateRange ? `Videos from ${dateRange.start} to ${dateRange.end}` : 'Last 6 months videos from all channels'}
              </p>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setActiveTab('sync')}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                activeTab === 'sync'
                  ? 'bg-brandColor text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Youtube className="h-4 w-4" />
              Fetch & Sync
            </button>
            <button
              onClick={() => setActiveTab('link')}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors border-l border-gray-200 ${
                activeTab === 'link'
                  ? 'bg-brandColor text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Link2 className="h-4 w-4" />
              Link to Schools
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors border-l border-gray-200 ${
                activeTab === 'upload'
                  ? 'bg-brandColor text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Upload className="h-4 w-4" />
              Upload Video
            </button>
          </div>

          {/* Right Side - Actions (only on sync tab) */}
          {activeTab === 'sync' && (
          <div className="flex items-center gap-3">
            <button
              onClick={fetchVideos}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            {unsyncedVideos.length > 0 && (
              <button
                onClick={handleSyncAll}
                disabled={syncing || loading}
                className="bg-brandColor text-white px-4 py-2 rounded-md hover:bg-opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {syncing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <CloudUpload className="h-4 w-4" />
                    Sync All ({unsyncedVideos.length})
                  </>
                )}
              </button>
            )}
          </div>
          )}
        </div>
      </div>

      {activeTab === 'link' && <div className="flex-1 overflow-hidden"><LinkToSchools /></div>}

      {activeTab === 'upload' && <div className="flex-1 overflow-hidden"><UploadVideos /></div>}

      {/* Filters — sync tab only */}
      {activeTab === 'sync' && <div className="bg-white border-b border-gray-200 px-8 py-3">
        <div className="flex items-center gap-4">
          <Filter className="h-4 w-4 text-gray-400" />

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Channel:</span>
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-brandColor focus:border-brandColor"
            >
              <option value="all">All Channels</option>
              {channels.map((channel) => (
                <option key={channel.key} value={channel.key}>
                  {channel.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Status:</span>
            <select
              value={syncFilter}
              onChange={(e) => setSyncFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-brandColor focus:border-brandColor"
            >
              <option value="all">All</option>
              <option value="synced">Synced</option>
              <option value="unsynced">Not Synced</option>
            </select>
          </div>

          <span className="text-sm text-gray-400 ml-auto">
            Showing {filteredVideos.length} of {videos.length} videos
          </span>
        </div>
      </div>}

      {/* Content — sync tab only */}
      {activeTab === 'sync' && <div className="flex-1 overflow-auto px-4 py-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-brandColor mb-4" />
            <p className="text-gray-500">Fetching videos from all channels...</p>
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
            <Youtube className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No videos found matching your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredVideos.map((video) => (
              <div
                key={video.video_id}
                className={`bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-shadow ${
                  video.is_synced ? 'border-green-200' : 'border-orange-200'
                }`}
              >
                <div className="relative">
                  <img
                    src={video.thumbnail_medium || video.thumbnail_high}
                    alt={video.title}
                    className="w-full h-36 object-cover"
                  />
                  <span className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                    {video.duration_formatted}
                  </span>
                  <span className="absolute top-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                    {video.city_name}
                  </span>
                </div>

                <div className="p-3">
                  <h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-2" title={video.title}>
                    {video.title}
                  </h3>

                  <div className="text-xs text-gray-500 mb-3">
                    <p>{new Date(video.published_at).toLocaleDateString()}</p>
                    <p>{formatNumber(video.view_count)} views • {formatNumber(video.like_count)} likes</p>
                  </div>

                  <div className="flex items-center justify-between">
                    {video.is_synced ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                        <Check className="h-3 w-3" />
                        Synced
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSyncVideo(video)}
                        disabled={syncingVideoId === video.video_id}
                        className="inline-flex items-center gap-1 text-xs text-white bg-brandColor hover:bg-opacity-90 px-3 py-1.5 rounded disabled:opacity-50"
                      >
                        {syncingVideoId === video.video_id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <CloudUpload className="h-3 w-3" />
                        )}
                        Sync
                      </button>
                    )}

                    <a
                      href={video.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-brandColor"
                      title="Watch on YouTube"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>}
    </div>
  );
};

export default YoutubeVideosAdmin;
